import type { ChapterItem } from '@app/_proto/Protos/chapters';
import type { NovelItem } from '@app/_proto/Protos/novels';
import { CHAPTER_EVENT_SOURCES, EVENT_SOURCES } from '@app/analytics/constants';
import { analyticsFactory } from '@app/analytics/handlers';
import Divider from '@app/components/Divider';
import { useChapterSettings } from '@app/components/chapters/hooks';
import EntityCommentsArea, { SortOptions } from '@app/components/entity-comments/commentsarea';
import NovelRelatedNovels from '@app/components/novel/novel.related.novels';
import { ChapterEvents } from '@app/domains/chapter/analytics/posthog/events';
import { chapterCommonLoggingParamsParser } from '@app/domains/chapter/analytics/posthog/handlers';
import ChapterContent from '@app/domains/chapter/components/ChapterContent';
import ChapterContentPage from '@app/domains/chapter/components/ChapterContentPage';
import ChapterHeader from '@app/domains/chapter/components/ChapterHeader';
import ChapterLinks from '@app/domains/chapter/components/ChapterLinks';
import { setAnonChaptersRead } from '@app/domains/chapter/utils';
import { AnalyticsTracking } from '@app/domains/common/components/AnalyticsTracking';
import useChapterLockStatus from '@app/domains/common/hooks/useChapterLockStatus';
import useChapterStatus from '@app/domains/common/hooks/useChapterStatus';
import usePricingModelStatus from '@app/domains/common/hooks/usePricingModelStatus';
import useUserStatus from '@app/domains/common/hooks/useUserStatus';
import { novelCommonLoggingParamsParser } from '@app/domains/novel/analytics/posthog/handlers';
import NovelLatestAnnouncement from '@app/domains/novel/components/NovelLatestAnnouncement';
import RelatedNovels from '@app/domains/novel/containers/RelatedNovels';
import { timestampToDate } from '@app/libs/utils';
import { makeAbsoluteUrl, useSchemaWith } from '@app/utils/schema';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import type { Article, WithContext } from 'schema-dts';
type ChapterProps = {
    novel: NovelItem;
    chapter?: ChapterItem;
};

export default function Chapter({ novel, chapter }: ChapterProps) {
    const { user } = useUserStatus();
    const [settings] = useChapterSettings();

    const { hasChapter, hasPrevChapter } = useChapterStatus({ chapter });

    const { isWtuCompleted } = usePricingModelStatus({
        novel,
        chapter,
    });

    const { isChapterLocked, isLockedByAdvancedChapter } = useChapterLockStatus({ novel, chapter });

    const location = useLocation();
    const queryClient = useQueryClient();

    //Increment the anonymous chapters read counter
    useEffect(() => {
        if (user || !chapter || !novel) {
            return;
        }

        if (chapter?.isTeaser || chapter?.karmaInfo?.isKarmaRequired || chapter.sponsorInfo?.advanceChapter) {
            return;
        }

        if (novel.isSneakPeek) {
            return;
        }

        if (novel) {
            setAnonChaptersRead(novel.slug, chapter.slug);
        }
    }, [user?.id, chapter, novel, user]);

    useEffect(() => {
        if (novel && isWtuCompleted) {
            const invalidate = () =>
                Promise.all([
                    queryClient.invalidateQueries(['chapterGroups', novel.slug]),
                    queryClient.invalidateQueries(['novel', novel.slug]),
                ]);

            invalidate();
        }
    }, [isWtuCompleted, novel, queryClient]);

    const handleCommentSort = useCallback(
        (sortValue: string) => {
            analyticsFactory(ChapterEvents.ClickChapterCommentSort)({
                novel,
                chapter,
                sortBy: SortOptions.find(option => option.value === sortValue)?.label,
            });
        },
        [chapter, novel]
    );

    const handleCommentSubmit = useCallback(() => {
        analyticsFactory(ChapterEvents.ClickSubmitYourChapterComment)({ novel, chapter });
    }, [chapter, novel]);

    const chapterPageTitle = chapter ? `${chapter.novelInfo?.name} - ${chapter.name}` : 'Loading...';

    return (
        <div
            data-posthog-params={JSON.stringify({
                ...novelCommonLoggingParamsParser(novel),
                ...chapterCommonLoggingParamsParser(chapter),
            })}
            className="overflow-x-hidden"
        >
            <Helmet
                prioritizeSeoTags
                script={useSchemaWith(
                    { novel, chapter },
                    ({ novel, chapter }): WithContext<Article> => ({
                        '@context': 'https://schema.org',
                        '@type': 'Article',
                        url: makeAbsoluteUrl(location.pathname),
                        headline: `${novel?.name} - ${chapter?.name}`,
                        datePublished:
                            timestampToDate(chapter?.whenToPublish)?.toISOString() ??
                            timestampToDate(chapter?.publishedAt)?.toISOString(),
                        publisher: {
                            '@type': 'Organization',
                            name: 'Wuxiaworld',
                            url: makeAbsoluteUrl(''),
                            // logo: makeAbsoluteUrl(''), // TODO
                        },
                        isAccessibleForFree: !isChapterLocked,
                        hasPart: {
                            '@type': 'WebPageElement',
                            isAccessibleForFree: !isChapterLocked,
                            cssSelector: '.chapter-content',
                        },
                        author: novel?.authorName?.value
                            ? {
                                  '@type': 'Person',
                                  name: novel.authorName.value,
                              }
                            : undefined,
                    })
                )}
            >
                <title>{chapterPageTitle}</title>
            </Helmet>
            <ChapterContentPage>
                {!!chapter && (
                    <AnalyticsTracking
                        title={chapterPageTitle}
                        extraProperties={{
                            dimension2: novel.translatorName?.value || novel.translator?.userName || null,
                            dimension4: novel.slug,
                            novel: novel.name,
                            translator: novel.translatorName?.value || novel.translator?.userName || null,
                        }}
                    />
                )}
                <div css={{ '& >div': { maxWidth: '840px', marginRight: 'auto', marginLeft: 'auto' } }}>
                    <ChapterHeader novel={novel} chapter={chapter} />
                    {!!chapter && <ChapterContent novel={novel} chapter={chapter} />}
                    <ChapterLinks
                        novel={novel}
                        chapter={chapter}
                        renderedFrom={CHAPTER_EVENT_SOURCES.ChapterViewBottom}
                    />
                    <div className={clsx('md:mt-40', { 'mt-20': !hasPrevChapter, 'mt-28': hasPrevChapter })}>
                        <NovelLatestAnnouncement
                            classes={{
                                collapse: 'mb-40',
                            }}
                            novelId={novel.id}
                            novelSlug={novel.slug}
                            novelStatus={novel.status}
                        />
                    </div>
                </div>
                <Divider isFullScreen className="lg:w-[1024px]" />
                {!!novel && !!chapter && !isLockedByAdvancedChapter && (
                    <EntityCommentsArea
                        key={`comments_${novel.slug}_${chapter.slug}`}
                        entityId={chapter.entityId}
                        disabled={!hasChapter || isChapterLocked}
                        disabledMessage={
                            isChapterLocked
                                ? 'Commenting is disabled until the chapter is unlocked. Apologies for the inconvenience.'
                                : ''
                        }
                        className="!border-b-0 pb-[40px] pt-[17px] sm:pt-[24px]"
                        onSort={handleCommentSort}
                        onCommentSubmit={handleCommentSubmit}
                    />
                )}
                <div
                    data-posthog-params={JSON.stringify({
                        on: EVENT_SOURCES.ChapterViewer,
                    })}
                >
                    <RelatedNovels>
                        <RelatedNovels.Title />
                        <div className="pt-12 md:pt-16">
                            <NovelRelatedNovels novel={novel} />
                        </div>
                    </RelatedNovels>
                </div>
            </ChapterContentPage>
        </div>
    );
}
